import * as React from 'react';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton, TextField } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { SavingState } from 'api/savingState';
import { useSelector } from 'react-redux';
import {
    Betreuer,
    addBetreuer,
    updateBetreuer,
    useBetreuerOne,
    defaultBetreuer,
    defaultBetreuerWorkTime,
    BetreuerWorkTime,
} from 'api/betreuer';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { isEqual } from 'lodash';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { LandByLkz2 } from 'formComponents/lookups/LaenderByIkz2';
import { betreuerByKzSelector } from 'store/lookups/selector';
import { logDifference } from 'utils/helper';
import { Rolle } from 'formComponents/lookups/Rolle';
import { weekdays, minutesToTimeString, timeStringToMinutes } from 'utils/time';
import classnames from 'classnames';
import { authSelector } from 'store/auth/selector';

const styles = mergeStyleSets({
    nameLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr 2fr 1fr',
        gridColumnGap: 10,
    },
    worktimes: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
        gridColumnGap: 10,
        gridRowGap: 5,
    },
    worktimesTitle: {
        fontWeight: 700,
    },
    worktimesLabel: {
        marginTop: 6,
    },
});

export interface BetreuerEditData {
    betrNr: number;
}

interface BetreuerEditProps {
    betrNr: number; // -1 = new
    onClose: (refetch: boolean) => void;
    onReopen: (betrNr: number) => void;
}

export const BetreuerEdit: React.FC<BetreuerEditProps> = ({ betrNr, onClose, onReopen }) => {
    const auth = useSelector(authSelector);
    const betreuerByKz = useSelector(betreuerByKzSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);

    const [betreuer, setBetreuer] = React.useState<Betreuer | null>(() => ({
        ...defaultBetreuer,
        betreuerWorkTimeList: [],
    }));

    const dbBetreuer = useBetreuerOne(
        {
            nr: betrNr,
            withBetreuerWorkTimeList: true,
        },
        {
            enabled: betrNr !== -1,
            onSuccess: (loadedBetreuer) => {
                setBetreuer({
                    ...loadedBetreuer,
                    betreuerWorkTimeList: loadedBetreuer.betreuerWorkTimeList.map((wt) => ({
                        ...wt,
                        startTimeS: minutesToTimeString(wt.startTime) || '',
                        stopTimeS: minutesToTimeString(wt.stopTime) || '',
                    })),
                });
                setSavingState(SavingState.notChanged);
            },
        },
    );

    /*
    React.useEffect(() => {
        if (!betreuerOne.isLoading && !betreuerOne.isFetching && betreuer === null) {
            if (betreuerOne.data) {
                setBetreuer({
                    ...betreuerOne.data,
                    betreuerWorkTimeList: betreuerOne.data.betreuerWorkTimeList.map((wt) => ({
                        ...wt,
                        startTimeS: minutesToTimeString(wt.startTime) || '',
                        stopTimeS: minutesToTimeString(wt.stopTime) || '',
                    })),
                });
                setSavingState(SavingState.notChanged);
            } else {
                setBetreuer({
                    ...defaultBetreuer,
                    betreuerWorkTimeList: [],
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [betreuer, betreuerOne]);
     */

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !dbBetreuer.isLoading &&
            !dbBetreuer.isFetching &&
            betreuer !== null
        ) {
            const cleanedBetreuer = {
                ...betreuer,
                betreuerWorkTimeList: betreuer.betreuerWorkTimeList.map((wt) => ({
                    betrNr: wt.betrNr,
                    weekday: wt.weekday,
                    block: wt.block,
                    startTime: wt.startTime,
                    stopTime: wt.stopTime,
                })),
            };

            if (!isEqual(dbBetreuer.data, cleanedBetreuer)) {
                setSavingState(SavingState.notSaved);
                logDifference('betreuer', cleanedBetreuer, dbBetreuer.data);
            }
        }
    }, [savingState, dbBetreuer, betreuer]);

    const validate = React.useCallback((): boolean => {
        if (!betreuer?.kz.trim()) {
            setValidationError('Bitte geben Sie ein Kürzel ein.');
            return false;
        }
        if (betreuer?.nr === -1 && betreuerByKz[betreuer?.kz.trim().toUpperCase()]) {
            setValidationError('Kürzel schon vergeben.');
            return false;
        }
        if (!betreuer?.name.trim()) {
            setValidationError('Bitte geben Sie einen Namen ein.');
            return false;
        }

        let wtListError = '';

        (betreuer?.betreuerWorkTimeList || []).forEach((wt) => {
            if (wt.startTime === null && wt.stopTime !== null) {
                wtListError = 'Start-Zeit nicht gesetzt.';
            } else if (wt.startTime !== null && wt.stopTime === null) {
                wtListError = 'Ende-Zeit nicht gesetzt.';
            } else if (wt.startTime !== null && wt.stopTime !== null) {
                if (wt.stopTime < wt.startTime) {
                    wtListError = 'Ende-Zeit vor Start-Zeit.';
                } else if (wt.stopTime === wt.startTime) {
                    wtListError = 'Ende-Zeit gleich Start-Zeit.';
                }
            }
        });

        if (wtListError) {
            setValidationError(wtListError);
            return false;
        }

        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [betreuer, betreuerByKz, validationError]);

    const save = React.useCallback(() => {
        if (!betreuer) {
            return;
        }

        if (savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);

                const cleanedBetreuer: Betreuer = {
                    ...betreuer,
                    betreuerWorkTimeList: betreuer.betreuerWorkTimeList.filter(
                        (wt) => wt.startTime !== null && wt.stopTime !== null,
                    ),
                };

                if (betrNr === -1) {
                    addBetreuer(cleanedBetreuer)
                        .then((result) => {
                            setSavingState(SavingState.saved);
                            /*
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                             */

                            onReopen(result.newNr);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateBetreuer(cleanedBetreuer)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            /*
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                             */
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    }, [betreuer, savingState, validate, betrNr, onReopen, onClose]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Betreuer, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(betreuer?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (betreuer) {
                    setBetreuer({
                        ...betreuer,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    /*
    const renderDecimalField = (column: keyof Betreuer, label: string, options: FormDecimalFieldOptions = {}) => (
        <FormDecimalField
            value={(betreuer?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (betreuer) {
                    setBetreuer({
                        ...betreuer,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );
     */

    const renderCheckbox = (column: keyof Betreuer, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={betreuer?.[column] as boolean}
            onChange={(event, checked) => {
                if (betreuer) {
                    setBetreuer({
                        ...betreuer,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderLand = () => (
        <LandByLkz2
            value={betreuer?.land || ''}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                if (betreuer) {
                    setBetreuer({
                        ...betreuer,
                        land: newValue,
                    });
                }
            }}
        />
    );

    const renderRolle = () => (
        <Rolle
            value={betreuer?.rolle || ''}
            disabled={savingState === SavingState.saving || auth.rolle !== 'GF'}
            onChange={(newValue): void => {
                if (betreuer) {
                    setBetreuer({
                        ...betreuer,
                        rolle: newValue,
                    });
                }
            }}
        />
    );

    const renderTimeValue = (minutes: number) => (
        <TextField value={minutesToTimeString(minutes, true) || ''} disabled />
    );

    const renderTime = (weekday: number, block: number, field: 'startTime' | 'stopTime') => {
        const betreuerWorkTime = (betreuer?.betreuerWorkTimeList || []).find(
            (wt) => wt.weekday === weekday && wt.block === block,
        );

        const fieldS = field === 'startTime' ? 'startTimeS' : 'stopTimeS';

        return (
            <TextField
                value={betreuerWorkTime?.[fieldS] || ''}
                disabled={savingState === SavingState.saving}
                onChange={(event, newValue): void => {
                    const cleanedNewValue = (newValue === undefined ? '' : newValue).replaceAll(/[^0-9:]/g, '');

                    if (betreuer) {
                        const existingBetreuerWorkTime = betreuer?.betreuerWorkTimeList.find(
                            (wt) => wt.weekday === weekday && wt.block === block,
                        );

                        const newBetreuerWorkTime: BetreuerWorkTime = existingBetreuerWorkTime
                            ? {
                                  ...existingBetreuerWorkTime,
                              }
                            : {
                                  ...defaultBetreuerWorkTime,
                                  betrNr: betreuer.nr,
                                  weekday,
                                  block,
                              };

                        newBetreuerWorkTime[fieldS] = cleanedNewValue;

                        setBetreuer({
                            ...betreuer,
                            betreuerWorkTimeList: [
                                ...betreuer?.betreuerWorkTimeList.filter(
                                    (wt) => !(wt.weekday === weekday && wt.block === block),
                                ),
                                newBetreuerWorkTime,
                            ],
                        });
                    }
                }}
                onBlur={() => {
                    if (betreuer) {
                        setBetreuer({
                            ...betreuer,
                            betreuerWorkTimeList: betreuer.betreuerWorkTimeList.map((wt) => {
                                const startTime =
                                    (wt.startTimeS || '').trim() === ''
                                        ? null
                                        : timeStringToMinutes(wt.startTimeS) || 0;
                                const stopTime =
                                    (wt.stopTimeS || '').trim() === '' ? null : timeStringToMinutes(wt.stopTimeS) || 0;

                                return {
                                    ...wt,
                                    startTime,
                                    startTimeS: minutesToTimeString(startTime) || '',
                                    stopTime,
                                    stopTimeS: minutesToTimeString(stopTime) || '',
                                };
                            }),
                        });
                    }
                }}
            />
        );
    };

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={(betreuer?.kz || '') + ' - ' + (betreuer?.name || betreuer?.firma || '')}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="80%"
                maxWidth="80%"
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.topBlock}>{renderCheckbox('aktiv', 'Aktiv')}</div>

                    <div className={styles.nameLine}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('kz', 'Kürzel', { maxLength: 3, disabled: betrNr !== -1 })}
                        </div>
                        <div className={formStyles.gridCell}>{renderTextField('name', 'Name', { maxLength: 250 })}</div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('firma', 'Firma', { maxLength: 50 })}
                        </div>
                        <div className={formStyles.gridCell}>{renderRolle()}</div>
                    </div>
                    <div className={formStyles.grid3}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('mail', 'E-Mail', { maxLength: 50 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('telefon', 'Telefon', { maxLength: 250 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('mobil', 'Mobil', { maxLength: 250 })}
                        </div>
                    </div>
                    <div className={formStyles.grid4}>
                        <div className={formStyles.gridCell}>{renderLand()}</div>
                        <div className={formStyles.gridCell}>{renderTextField('plz', 'PLZ', { maxLength: 10 })}</div>
                        <div className={formStyles.gridCell}>{renderTextField('ort', 'Ort', { maxLength: 50 })}</div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('strasse', 'Strasse', { maxLength: 50 })}
                        </div>
                    </div>
                    <h3>Arbeitszeiten</h3>
                    {/*
                    <div className={formStyles.grid4}>{renderDecimalField('holidayHours', 'Urlaubsstunden')}</div>
                    */}
                    <div className={styles.worktimes}>
                        <div className={styles.worktimesTitle}>Wochentag</div>
                        <div className={styles.worktimesTitle}>Vormittag Von</div>
                        <div className={styles.worktimesTitle}>Vormittag Bis</div>
                        <div className={styles.worktimesTitle}>Nachmittag Von</div>
                        <div className={styles.worktimesTitle}>Nachmittag Bis</div>
                        <div className={styles.worktimesTitle}>Tagesstunden</div>
                        {weekdays.map((weekday, index) => {
                            return (
                                <React.Fragment key={weekday}>
                                    <div className={styles.worktimesLabel}>{weekday}</div>
                                    <div>{renderTime(index, 0, 'startTime')}</div>
                                    <div>{renderTime(index, 0, 'stopTime')}</div>
                                    <div>{renderTime(index, 1, 'startTime')}</div>
                                    <div>{renderTime(index, 1, 'stopTime')}</div>
                                    <div>
                                        {renderTimeValue(
                                            (betreuer?.betreuerWorkTimeList || [])
                                                .filter((wt) => wt.weekday === index)
                                                .reduce((sum, wt) => {
                                                    if (wt.startTime !== null && wt.stopTime !== null) {
                                                        return sum + wt.stopTime - wt.startTime;
                                                    }
                                                    return sum;
                                                }, 0),
                                        )}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        <div />
                        <div />
                        <div />
                        <div />
                        <div className={classnames(styles.worktimesTitle, styles.worktimesLabel)}>
                            Soll-Wochenstunden
                        </div>
                        <div>
                            {renderTimeValue(
                                (betreuer?.betreuerWorkTimeList || []).reduce((sum, wt) => {
                                    if (wt.startTime !== null && wt.stopTime !== null) {
                                        return sum + wt.stopTime - wt.startTime;
                                    }
                                    return sum;
                                }, 0),
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbBetreuer.isLoading}
                            />
                        )}
                    </DialogFooter>

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <Timestamps
                        modifiedDate={betreuer?.updDat}
                        modifiedDecimalTime={betreuer?.updUhr}
                        modifiedUser={betreuer?.updUser}
                        showCreated={false}
                    />
                </form>
                {(dbBetreuer.isLoading || dbBetreuer.isFetching || !betreuer) && <LoadingIndicator />}
            </Dialog>
        </>
    );
};
